import { useEffect, useState } from 'react';
import { Zap } from '@ui/shared/icons';

/**
 * XP toast (badges-and-celebrations.md SS2) — the atomic "your effort
 * counted" unit. Bottom-anchored pill, self-dismissing, never interactive,
 * never blocks taps, never stacks (a new award while one is showing
 * coalesces into the existing toast rather than mounting a second one — see
 * `useCelebrationQueue`'s coalescing logic).
 *
 * Reduced motion: fade in/out only (tokens.md SS"prefers-reduced-motion" is
 * handled globally by tokens.css's `@media (prefers-reduced-motion: reduce)`
 * block, which already collapses every transition-duration to
 * --dur-reduced-utility — this component adds no bespoke motion logic beyond
 * what that global rule already covers, matching PulseLine/ProgressRing's
 * approach of leaning on the global CSS override rather than JS media-query
 * branching).
 */
export function XPToast({
  amount,
  sourceLabel,
  onDismiss,
}: {
  readonly amount: number;
  readonly sourceLabel: string;
  readonly onDismiss: () => void;
}) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const holdTimer = window.setTimeout(() => setVisible(false), 1800);
    return () => window.clearTimeout(holdTimer);
  }, [amount, sourceLabel]);

  useEffect(() => {
    if (visible) return;
    const exitTimer = window.setTimeout(onDismiss, 200);
    return () => window.clearTimeout(exitTimer);
  }, [visible, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'calc(var(--space-8) + env(safe-area-inset-bottom, 0px))',
        transform: `translateX(-50%) translateY(${visible ? '0' : '-4px'})`,
        opacity: visible ? 1 : 0,
        transition: `opacity var(--dur-toast-out) var(--ease-exit), transform var(--dur-toast-out) var(--ease-exit)`,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        minHeight: 'var(--touch-target-min)',
        padding: '0 var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-overlay)',
        boxShadow: 'var(--shadow-3)',
        border: '1px solid var(--border-subtle)',
        pointerEvents: 'none',
        maxWidth: '220px',
        whiteSpace: 'nowrap',
      }}
    >
      <Zap size={16} color="var(--hue-primary-vivid)" aria-hidden="true" />
      <span className="num" style={{ fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>
        +{amount} XP
      </span>
      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
        {sourceLabel}
      </span>
    </div>
  );
}
